/**
 * The fire — what burns, for how long, what it leaves, and where it keeps it.
 *
 * **T108 · D86 · D89 · D90 · D96 · [ADR-0017](../../../Project/adr/0017-the-hearth-is-what-death-cannot-reach.md)**
 *
 * ## 🔴 Everything here is pure, and that is not a style choice
 *
 * **There is no database on this workstation and the server cannot be run
 * locally at all.** So the whole of the fire's thinking lives in this file,
 * where it can be tested, and the server file that calls it is plumbing.
 *
 * ## 🎯 D96 — six slots, and they are not interchangeable
 *
 * **haugur, 2026-08-27:** *"heart menu should show 2 slots for wood for burnig
 * that can hold 99 each, then we need 4 slots for charcoal to be saved to… that
 * adds the role of charcoal storage to the heath."*
 *
 * 🔴 **This SUPERSEDES D86's nine general slots.** ✅ **And it is a better idea
 * than the one it replaces:** the slots themselves enforce *wood only*, so
 * **D90's rule that charcoal cannot go in the fire stops needing an error
 * message and becomes a property of the shape.** The wood slots are what burns;
 * the charcoal slots are a store the fire fills.
 *
 * ## The house pattern, and why the fire does not tick
 *
 * ⚠️ **A fire that counted down would be a disaster in this codebase.** Fuel
 * burns against a clock, so a ticking number would have to be written into the
 * chunk every few seconds — turning one fire into a stream of saved edits, in a
 * table whose whole design is *only what a player changed*, for minutes at a
 * time while nobody was watching.
 *
 * ✅ **Instead the fire writes down WHEN the current log started burning and HOW
 * MUCH wood is in it, and the answer is worked out when somebody looks.**
 *
 * ## ⚠️ Why `litAtGameHours` ADVANCES rather than resetting
 *
 * {@link resolveHearth} moves the marker forward by **whole logs only**. That is
 * the difference between a fair fire and one that quietly robs you: if adding a
 * log reset the clock to now, the log already half burnt would start again from
 * the beginning, and **you would lose up to six game hours every time you fed
 * the fire.** Nobody would ever see it happen.
 */

import { ITEMS, cooksTo, itemById, itemIdForKey, type FuelDef } from './items.ts';
import { canGoIn, isClothingSlot, isValidSlot, stackLimit, type Stack } from './inventory.ts';
import { REAL_MS_PER_GAME_DAY } from './clock.ts';
import { TUNING } from './tuning.ts';
import { blockIdForKey } from './blocks.ts';
import { withState } from './states.ts';

/** 🪵 Slots the fire burns FROM. **D96.** */
export const HEARTH_WOOD_SLOTS = 2;

/**
 * ⚫🍲 **Slots the fire FILLS** — charcoal and cooked food together. **D96, and
 * widened by D121 on 2026-09-22.**
 *
 * 🔴 **It was called `HEARTH_CHARCOAL_SLOTS` until cooking existed**, and the
 * rename is not tidying. 🎯 **haugur's layout for the cooking screen:** *"2
 * boxes on the left for what is to be cooked, 2 boxes below for the fuel and 4
 * boxes on the right for the finished cooked product"* — **the same four boxes
 * charcoal was already going into.** ⚠️ **`06_Traps.md` rule 3: a name derived
 * from a proxy is wrong the day a second thing shares it.** A skemma was called
 * *Búr* because 54 slots meant *búr*; these were called charcoal slots because
 * charcoal was the only thing a fire made.
 */
export const HEARTH_OUTPUT_SLOTS = 4;

/**
 * 🍲 **Slots holding what is waiting to be cooked** — **D121, T222.**
 *
 * ⚠️ **Two, and they are haugur's own count**, matching the two fuel slots
 * above them on his screen.
 */
export const HEARTH_COOK_SLOTS = 2;

/**
 * 🔴 **The slot numbering, and it is APPENDED rather than rearranged.**
 *
 * | Slots | |
 * |---|---|
 * | **0–1** | 🪵 fuel — what burns |
 * | **2–5** | ⚫🍲 output — charcoal **and** cooked food |
 * | **6–7** | 🍲 input — what is waiting to be cooked |
 *
 * ⚠️ **The two new slots are 6–7 and NOT 0–1, although they are drawn at the
 * top left.** `hud-scene.ts` says it in as many words about the old six:
 * *"Order is meaning. Wood is 0–1 and charcoal is 2–5, and the protocol will
 * name slots by these numbers, so this must never be reordered."*
 * 🔴 **A slot number is an identity on the wire, never a position on the
 * glass** — renumbering would have moved the right item into the wrong box for
 * every client that had not caught up, with nothing raised anywhere.
 */
export const HEARTH_SLOT_COUNT = HEARTH_WOOD_SLOTS + HEARTH_OUTPUT_SLOTS + HEARTH_COOK_SLOTS;

/** Where the output slots begin. */
const OUTPUT_FROM = HEARTH_WOOD_SLOTS;
/** Where the cooking-input slots begin. */
const COOK_FROM = HEARTH_WOOD_SLOTS + HEARTH_OUTPUT_SLOTS;

/**
 * How much fits in one slot.
 *
 * ⚠️ **The game's own stack size, never a number typed here.** 99 everywhere
 * (haugur, 2026-08-13) — *"it gives a player the sense that something is full"*.
 */
export const HEARTH_STACK = TUNING.inventory.defaultStackSize;

/**
 * What one hearth remembers. **This is the side table's payload** — see
 * `packages/server/src/blockdata.ts`.
 */
export interface HearthState {
  /** Logs in each of the two wood slots. **What the fire burns.** */
  readonly wood: readonly number[];
  /**
   * 🪵 **WHICH wood is in each slot**, or `null` for an empty slot.
   *
   * 🔴 **ADDED 2026-09-02, AFTER haugur FOUND THE HEARTH LYING.** He fed it
   * driftwood and opened it: *"it lights the fire but if i open it up i dont see
   * the rekaviður there, i placed 2 but there where not 2 on stock in the
   * hearth."*
   *
   * ⚠️ **A slot stored a COUNT and nothing else**, so the panel had to guess
   * what the count was OF — and it guessed {@link HEARTH_FUEL}, birch. Feeding
   * the fire by right-click accepted driftwood happily, because that rule reads
   * the DATA; the panel refused it into a slot and drew birch for whatever was
   * already there, because that rule read a CONSTANT. **Two halves of one
   * feature disagreeing.**
   *
   * ✅ This is the fix this file named all along: *"the day a second wood
   * arrives, this is what fails, and the fix is per-slot fuel types rather than
   * a count."* ⚠️ The DURATION is still shared — birch and driftwood both burn
   * six game hours — **but the IDENTITY never was, and that is what was
   * missing.**
   *
   * ⚠️ **A hearth saved before today has no such field**, and `readHearth`
   * fills it with birch: the only wood that existed when the row was written.
   */
  readonly woodItem: readonly (string | null)[];
  /**
   * ⚫🍲 **What is in each of the four output slots. What the fire FILLS.**
   *
   * 🔴 **This field was called `charcoal` until 2026-09-22**, and a hearth saved
   * before then still has that name in its row — {@link readHearth} reads both.
   * ⚠️ **The rename is the whole of D121 in one line:** four slots that held one
   * known thing now hold five different ones, so a count on its own no longer
   * says what it is a count OF.
   */
  readonly output: readonly number[];
  /**
   * ⚫🍲 **WHICH thing is in each output slot**, or `null` for an empty one.
   *
   * 🔴 **Exactly the fault {@link HearthState.woodItem} was added to fix, one
   * column to the right.** 🎯 **haugur, 2026-09-02, about the wood:** *"i placed
   * 2 but there where not 2 on stock in the hearth."* A slot stored a count and
   * the panel had to guess what the count was of — and it guessed. **The fix is
   * written down in this file as a rule, and this is the second time it has been
   * needed.**
   *
   * ⚠️ **A hearth saved before 2026-09-22 has no such field**, and
   * {@link readHearth} fills it with charcoal: the only thing an output slot
   * could possibly have held when the row was written.
   */
  readonly outputItem: readonly (string | null)[];
  /**
   * 🍲 **How much raw food is waiting in each of the two input slots** — D121.
   */
  readonly cook: readonly number[];
  /** 🍲 **WHICH raw food is in each input slot**, or `null` for an empty one. */
  readonly cookItem: readonly (string | null)[];
  /**
   * 🍲 **Absolute game hours at which the CURRENT item started cooking**, or
   * `null` when nothing is cooking.
   *
   * 🔴 **It exists for exactly the reason {@link HearthState.litAtGameHours}
   * does, and it advances the same way** — by whole cooked items, never reset to
   * *now*. ⚠️ **If it reset every time you added a fish, the fish already half
   * cooked would start again from the beginning**, and nobody would ever see it
   * happen. That is this file's oldest warning, applied to the second clock it
   * has ever had.
   *
   * 🔴 **It is NOT the same as `litAtGameHours` and must not be derived from
   * it.** A fire lit an hour before you put a fish in has been burning for an
   * hour; the fish has not been cooking for an hour. **Deriving it would have
   * cooked the fish the instant it was dropped in, which looks like the feature
   * working.**
   */
  readonly cookFromGameHours: number | null;
  /**
   * Absolute game hours at which the **current** log started burning, or `null`
   * when nothing is burning.
   *
   * ⚠️ **Not "when the fire was lit"** — it advances one log-length at a time,
   * which is what preserves a part-burnt log across a feed. See the module note.
   */
  readonly litAtGameHours: number | null;
}

/** A hearth nobody has touched: cold, empty, and still your home (D86). */
export function emptyHearth(): HearthState {
  return {
    wood: new Array<number>(HEARTH_WOOD_SLOTS).fill(0),
    woodItem: new Array<string | null>(HEARTH_WOOD_SLOTS).fill(null),
    output: new Array<number>(HEARTH_OUTPUT_SLOTS).fill(0),
    outputItem: new Array<string | null>(HEARTH_OUTPUT_SLOTS).fill(null),
    cook: new Array<number>(HEARTH_COOK_SLOTS).fill(0),
    cookItem: new Array<string | null>(HEARTH_COOK_SLOTS).fill(null),
    litAtGameHours: null,
    cookFromGameHours: null,
  };
}

/**
 * 🔥 **What this fire is allowed to do.** The hearth and the búr are the same
 * machine standing in two places — **D121** — and this is the one line on which
 * they differ.
 */
export interface FireRules {
  /**
   * ⚫ **Whether burning a log leaves charcoal in the output.**
   *
   * 🎯 **haugur, 2026-09-22, asked because it collides with a locked decision:**
   * the hearth makes charcoal and **the búr does not.** 🔴 **D71 and D109 make
   * the búr the one building that refuses everything which is not food**, so a
   * búr quietly filling with charcoal would have been that rule broken by its
   * own fire. ✅ **His answer gives each place a job:** *the búr is the kitchen,
   * the hearth is the kiln.*
   */
  readonly makesCharcoal: boolean;
  /**
   * 🔥 **Whether it lights ITSELF, like a furnace — and goes out when the food
   * is done.**
   *
   * 🔴 **THE BÚR'S FIRE COULD NEVER LIGHT.** Only {@link addLog} lights a fire,
   * and only a right-click on a HEARTH with wood in hand reaches it — a
   * right-click on a búr opens its menu. **Its own screen said *"Put wood in and
   * it lights itself"* from the day it shipped, and nothing did.** 📏 **The
   * server had never once logged a búr fire burning** (`bur_fire_burnt`, zero
   * since 1 September). ⚠️ **Every búr test began with the fire already lit**,
   * which is why none of them could see it.
   *
   * 🎯 **haugur chose the furnace, 2026-09-28**, over *"like the hearth"* and
   * *"lights when wood goes in"*: 📖 Minecraft's furnace burns only while it
   * has something to smelt. ✅ **So: wood AND food → it lights. The food is done,
   * or the shelf is full → it finishes the log it is on, and goes out.** Wood is
   * spent only on cooking.
   */
  readonly lightsItself: boolean;
  /**
   * 🍲 **Whether it cooks at all.** 🏠 **D135, 2026-10-05**: the pit-house
   * oven is a fire that lights and warms a room and does NOT cook — 🎯 haugur
   * chose *"a real fire you light"* over *"a fire that also cooks"*, because a
   * vefstofa is not a second kitchen. ⚠️ So its cook slots and its shelf take
   * nothing, and a fire with this false never moves food.
   */
  readonly cooks: boolean;
}

/** 🔥 The hearth: it cooks **and** it makes charcoal, and YOU light it. */
export const HEARTH_RULES: FireRules = { makesCharcoal: true, lightsItself: false, cooks: true };

/** 🥫 The búr's fire: it cooks, leaves nothing behind, and lights itself. **D121.** */
export const BUR_FIRE_RULES: FireRules = { makesCharcoal: false, lightsItself: true, cooks: true };

/** 🔥 The pit-house oven: YOU light it, it warms and lights the room, and it cooks nothing and leaves nothing. **D135.** */
export const OVEN_RULES: FireRules = { makesCharcoal: false, lightsItself: false, cooks: false };

/**
 * 🔥 **Light a furnace-like fire if it is ready** — wood in, food in, not
 * already alight. **Anything else comes back untouched**, including every
 * hearth, which is lit by hand.
 *
 * 🔴 **Called after every move into a búr's fire AND at the top of every
 * resolve** — the second is what rescues a fire that was loaded before this
 * rule existed and has sat cold ever since.
 */
export function kindle(state: HearthState, nowGameHours: number, rules: FireRules): HearthState {
  if (!rules.lightsItself || state.litAtGameHours !== null) return state;
  if (woodTotal(state) <= 0 || cookTotal(state) <= 0) return state;
  return {
    ...state,
    litAtGameHours: nowGameHours,
    cookFromGameHours: state.cookFromGameHours ?? nowGameHours,
  };
}

/** How many logs are in the fire altogether. */
export function woodTotal(state: HearthState): number {
  return state.wood.reduce((sum, n) => sum + n, 0);
}

/**
 * How much charcoal the hearth is holding.
 *
 * ⚠️ **It counts the output slots that actually hold charcoal**, where it used
 * to be able to add up all four — since D121 an output slot may hold a cooked
 * fish instead, and adding those in would have reported them as charcoal.
 */
export function charcoalTotal(state: HearthState): number {
  const ash = ashOf(HEARTH_FUEL);
  if (ash === undefined) return 0;
  return state.output.reduce((sum, n, i) => (state.outputItem.at(i) === ash ? sum + n : sum), 0);
}

/** How much raw food is waiting to be cooked. */
export function cookTotal(state: HearthState): number {
  return state.cook.reduce((sum, n) => sum + n, 0);
}

/**
 * Absolute game hours from played real milliseconds.
 *
 * ⏱️ **One game hour is 50 real seconds** — the world runs at 72×, so a game day
 * is 20 real minutes (D41). ✅ **Which is why a birch log at 6 game hours is
 * exactly the 5 real minutes D86 says it is.**
 */
export function gameHoursIn(playedMs: number): number {
  return (playedMs / REAL_MS_PER_GAME_DAY) * 24;
}

/**
 * The fuel entry for an item key, or `undefined` if it does not burn.
 *
 * 🔴 **Being fuel is NOT the same as being accepted by the campfire.** Charcoal
 * has a fuel entry — 30 game hours — and **the campfire must refuse it** (D90).
 * See {@link campfireAccepts}.
 */
export function fuelFor(itemKey: string): FuelDef | undefined {
  const id = itemIdForKey(itemKey);
  return id === undefined ? undefined : itemById(id)?.fuel;
}

/**
 * 🔴 **Does the campfire take this? Wood yes, charcoal NO.**
 *
 * 🎯 **haugur, 2026-08-25, D90:** *"campfire needs trees as fuel, charcoal
 * cannot be used"*.
 *
 * ⚠️ **So this cannot be `fuelFor(key) !== undefined`.** Charcoal burns for 30
 * game hours and that number is real — it is what charcoal is worth in the stove
 * and in smelting, **neither of which exists yet.** A campfire that accepted it
 * would be reading the right field for the wrong machine.
 *
 * 👉 **The rule is: it burns, AND burning it leaves something behind.** Wood
 * leaves charcoal; charcoal leaves nothing, because converting is the end of the
 * chain and not a loop. ✅ **That is a property of the data rather than a list of
 * item keys**, so the day driftwood becomes fuel, nothing here changes.
 *
 * ✅ **D96 made this belt-and-braces:** the wood slots only accept wood, so the
 * shape of the menu refuses charcoal before this function is ever asked.
 */
export function campfireAccepts(itemKey: string): boolean {
  const fuel = fuelFor(itemKey);
  return fuel !== undefined && fuel.leavesBehind !== undefined;
}

/** What one accepted log is worth, in game hours. `0` if it is not accepted. */
export function burnGameHoursOf(itemKey: string): number {
  return campfireAccepts(itemKey) ? (fuelFor(itemKey)?.burnGameHours ?? 0) : 0;
}

/** What one burnt log leaves behind, or `undefined`. */
export function ashOf(itemKey: string): string | undefined {
  return campfireAccepts(itemKey) ? fuelFor(itemKey)?.leavesBehind : undefined;
}

/**
 * 🔴 **The one fuel the fire currently takes.**
 *
 * ⚠️ **A deliberate simplification, and it is written down rather than hidden.**
 * A hearth stores a count of logs per slot, not a queue of different fuels, so
 * every log in it must burn for the same length of time. **Today exactly one
 * item is both fuel and accepted by the campfire** — birch — so the
 * simplification costs nothing and a test asserts it stays true. 👉 **The day a
 * second wood arrives, this is what fails**, and the fix is per-slot fuel types
 * rather than a count.
 */
export const HEARTH_FUEL = 'birkistofn';

export interface HearthResolution {
  readonly state: HearthState;
  /** Logs that finished burning since last time. */
  readonly logsBurnt: number;
  /** Charcoal actually stored. */
  readonly charcoalMade: number;
  /**
   * 🔴 **Charcoal that had nowhere to go.**
   *
   * 🎯 **haugur, 2026-08-27**, asked what happens when the four slots fill:
   * **the fire keeps burning and the charcoal is lost.** ⚠️ *"A fire that stops
   * because a box is full is a fire that looks broken"* — and four slots is
   * **396 charcoal, about 33 real hours of burning**, so this is reachable in
   * principle and not in practice. **Reported so the server can log it if it
   * ever really happens.**
   */
  readonly charcoalLost: number;
  /**
   * 🍲 **How many raw things the fire turned into cooked ones** — D121, T222.
   */
  readonly cooked: number;
  /**
   * 🍲 **Why cooking stopped, when it did.** `null` means it did not.
   *
   * 🔴 **It exists because the two reasons need DIFFERENT words on screen**, and
   * *"nothing is happening"* is a bug report either way. **`noFood`** is the
   * ordinary end of a job; **`noRoom`** is the fire standing idle with food in
   * it, which a player cannot possibly diagnose by looking at a fire.
   */
  readonly cookingStopped: 'noFood' | 'noRoom' | null;
  /**
   * 🔴 **Whether anything at all moved, and the caller MUST write when it did.**
   *
   * ⚠️ **It used to be `logsBurnt > 0`, worked out by the server**, and that
   * stopped being the same question the moment a fire could cook: a fish can
   * finish inside one log's burn, and the cooking marker can move with no log
   * burnt at all. **A resolve whose result is not written is a resolve that
   * happens again from the same start**, which cooks the same fish twice.
   */
  readonly changed: boolean;
}

/**
 * Take `n` logs out of the wood slots, front first.
 *
 * ⚠️ `map` rather than an indexed loop, throughout this file. Indexing an array
 * with a variable is the object-injection pattern the linter flags, and writing
 * it this way answers the question instead of suppressing it — the same
 * reasoning the Maps in `blocks.ts` are there for.
 */
function takeWood(wood: readonly number[], n: number): number[] {
  let left = n;
  return wood.map((count) => {
    const taken = Math.min(count, left);
    left -= taken;
    return count - taken;
  });
}

/** What the output shelf holds, as a pair of arrays that always agree. */
interface Output {
  readonly output: readonly number[];
  readonly outputItem: readonly (string | null)[];
}

/**
 * ⚫🍲 **Put `n` of one thing onto the output shelf**, filling each slot in turn.
 *
 * 🔴 **The shelf holds five different things now — charcoal and four cooked
 * fish — so this has to check WHAT is in a slot and not just how much.** Before
 * D121 there was one known item and a count was enough.
 *
 * ⚠️ **A slot holding zero is empty whatever its name says.** The name is
 * cleared when a slot empties, but reading it that way costs nothing and means a
 * stale row can never make a free slot unusable.
 *
 * @returns the new shelf, how many fitted, and how many had nowhere to go.
 */
function putInOutput(shelf: Output, itemKey: string, n: number): Output & { stored: number } {
  let left = n;
  const limit = stackLimit(itemKey);
  const nextItem: (string | null)[] = [];
  const output = shelf.output.map((count, i) => {
    const here = count > 0 ? (shelf.outputItem.at(i) ?? null) : null;
    const room = here === null || here === itemKey ? limit - count : 0;
    const put = Math.min(Math.max(room, 0), left);
    left -= put;
    nextItem.push(put > 0 ? itemKey : here);
    return count + put;
  });
  return { output, outputItem: nextItem, stored: n - left };
}

/**
 * 🍲 **Cook one thing off the front of the input, if there is one and it fits.**
 *
 * @returns the new input and shelf, or **why it could not** — and the two
 *   reasons are kept apart because they need different words on screen.
 */
function cookOne(
  cook: readonly number[],
  cookItem: readonly (string | null)[],
  shelf: Output,
):
  | { cook: readonly number[]; cookItem: readonly (string | null)[]; shelf: Output }
  | 'noFood'
  | 'noRoom' {
  const at = cook.findIndex((n) => n > 0);
  if (at === -1) return 'noFood';

  const raw = cookItem.at(at) ?? null;
  /* 🔴 A slot holding something with no cooked form is treated as *nothing to
     cook*, never as an error. It cannot be reached through the panel — an input
     slot refuses anything without a `cooksTo` — but a hand-written database row
     can hold anything at all, and the honest answer for one is a fire that sits
     there rather than a hearth that throws for ever. */
  const becomes = raw === null ? undefined : cooksTo(raw);
  if (becomes === undefined) return 'noFood';

  const put = putInOutput(shelf, becomes, 1);
  if (put.stored === 0) return 'noRoom';

  return {
    cook: cook.map((n, i) => (i === at ? n - 1 : n)),
    /* ⚠️ A slot emptied to zero forgets what it held, so the next food is free
       to take it — the wood slots' own rule, one column over. */
    cookItem: cookItem.map((here, i) => (i === at && (cook.at(i) ?? 0) <= 1 ? null : here)),
    shelf: { output: put.output, outputItem: put.outputItem },
  };
}

/**
 * 🔥 **Bring a fire up to date. The whole of its arithmetic.**
 *
 * ## 🔴 Burning and cooking are ONE pass over ONE window, and they must be
 *
 * The fire can only cook while it is **alight**, so both are worked out across
 * the same stretch of time: from where each was last brought up to date, to
 * **whichever comes first — now, or the moment the wood runs out.**
 * ⚠️ **Cooking against `nowGameHours` instead would cook a fish over a fire that
 * went out an hour ago**, and nothing on screen would have looked wrong.
 *
 * ## 🔴 Cooked food is put on the shelf BEFORE charcoal, and that is the rule
 *
 * The two share four slots now — 🎯 haugur's own layout. **Cooked food that has
 * nowhere to go is NOT cooked**; the fish stays raw in its slot and waits.
 * **Charcoal that has nowhere to go is LOST**, which is 🎯 his ruling of
 * 2026-08-27: *"a fire that stops because a box is full is a fire that looks
 * broken."* ⚠️ **The asymmetry is deliberate and it is about who put it there.**
 * A player chose to put that fish in; nobody chose the charcoal.
 *
 * @param state what the side table remembers
 * @param nowGameHours absolute game hours, from {@link gameHoursIn}
 * @param rules whether this fire makes charcoal — {@link HEARTH_RULES} or
 *   {@link BUR_FIRE_RULES}. **D121.**
 */
export function resolveHearth(
  state: HearthState,
  nowGameHours: number,
  rules: FireRules = HEARTH_RULES,
): HearthResolution {
  const perLog = burnGameHoursOf(HEARTH_FUEL);
  const inFire = woodTotal(state);
  const still = (s: HearthState): HearthResolution => ({
    state: s,
    logsBurnt: 0,
    charcoalMade: 0,
    charcoalLost: 0,
    cooked: 0,
    cookingStopped: null,
    changed: s !== state,
  });

  /* Nothing burning, no fuel, or a fuel that burns for no time at all — the
     last of which would divide by zero and is a data error, not a game state.
     🔴 A fire that is not alight cannot be cooking, so the cooking marker goes
     with it — otherwise a fire relit tomorrow would finish today's fish on its
     first tick, having done none of the burning. */
  /* 🔥 A furnace that has become ready lights NOW — D121's búr, 2026-09-28.
     ⚠️ Before anything else, and returned as it is: it has only just caught,
     so no time has burned yet. */
  const kindled = kindle(state, nowGameHours, rules);
  if (kindled !== state) return still(kindled);

  if (state.litAtGameHours === null || inFire <= 0 || perLog <= 0) {
    return still(state.cookFromGameHours === null ? state : { ...state, cookFromGameHours: null });
  }

  const elapsed = nowGameHours - state.litAtGameHours;
  /* ⚠️ A clock that appears to go backwards is not impossible — the world clock
     counts PLAYED time and is restored from the database on restart. Treated as
     "no time passed" rather than as negative burning. */
  if (elapsed <= 0) return still(state);

  /* 🔥 The wood can carry the fire this far and no further. Everything below
     happens inside this window. */
  const burnUntil = Math.min(nowGameHours, state.litAtGameHours + inFire * perLog);

  /* 🍲 Cooking, across the same window. ⚠️ Worked out BEFORE the wood now,
     because a furnace's wood depends on when its cooking stopped. */
  const per = TUNING.cooking.cookGameHours;
  let shelf: Output = { output: state.output, outputItem: state.outputItem };
  let cook = state.cook;
  let cookItem = state.cookItem;
  let cooked = 0;
  let cookingStopped: 'noFood' | 'noRoom' | null = null;

  if (rules.cooks && state.cookFromGameHours !== null && per > 0) {
    const room = burnUntil - state.cookFromGameHours;
    const byTime = room > 0 ? Math.floor(room / per) : 0;
    while (cooked < byTime) {
      const step = cookOne(cook, cookItem, shelf);
      if (step === 'noFood' || step === 'noRoom') {
        cookingStopped = step;
        break;
      }
      cook = step.cook;
      cookItem = step.cookItem;
      shelf = step.shelf;
      cooked += 1;
    }
  }

  /**
   * 🔥 **When the burning STOPS inside this window.**
   *
   * A hearth burns until its wood runs out. 🔴 **A furnace (`lightsItself`)
   * also stops when its cooking does — at the end of the LOG it was on**, which
   * is Minecraft's own rule: a fuel item that has caught burns to the end, and no
   * new one is lit for nothing. ⚠️ **So the log in progress is spent, never
   * refunded**, and a búr loaded with 25 logs and one fish uses one log.
   */
  /* 🔴 Only a finished FURNACE goes out early. ⚠️ A test on times alone put
     out every hearth that was merely still burning — `now` is before the wood
     runs out in exactly that case. */
  let out = false;
  let burnsTo = burnUntil;
  if (rules.lightsItself) {
    const done =
      state.cookFromGameHours === null || cookingStopped !== null || cook.every((n) => n <= 0);
    if (done) {
      const stoppedAt =
        state.cookFromGameHours === null
          ? state.litAtGameHours
          : state.cookFromGameHours + cooked * per;
      /* The log in progress when it stopped — at least the first. A hair off
         the division so a stop exactly on a boundary ends THAT log, not the
         next. */
      const logs = Math.max(1, Math.ceil((stoppedAt - state.litAtGameHours) / perLog - 1e-9));
      const logEnd = state.litAtGameHours + logs * perLog;
      if (logEnd <= nowGameHours) {
        out = true;
        burnsTo = Math.min(burnUntil, logEnd);
      }
    }
  }

  const logsBurnt = Math.min(
    out ? Math.round((burnsTo - state.litAtGameHours) / perLog) : Math.floor(elapsed / perLog),
    inFire,
  );
  const wood = takeWood(state.wood, logsBurnt);
  /* ⚠️ A slot that has burnt empty forgets what it held, or it would go on
     declining the other wood for ever. */
  const woodItem = state.woodItem.map((here, i) => ((wood.at(i) ?? 0) > 0 ? here : null));
  const leftInFire = inFire - logsBurnt;
  /* 🔴 The marker ADVANCES by whole logs. It does not become `nowGameHours`,
     which would throw away the part-burnt log at the front — see the module
     note. When the last log goes, or a furnace has finished, the fire is out
     and there is no marker. */
  const litAtGameHours = leftInFire > 0 && !out ? state.litAtGameHours + logsBurnt * perLog : null;

  /**
   * 🍲 **Where the cooking marker lands, and the three cases are different.**
   *
   * | | |
   * |---|---|
   * | Nothing left to cook, or the fire is out | **no marker** — there is no job |
   * | Stopped because the shelf is full | **`burnUntil`** — the fire burned and cooked nothing, and **that time is spent rather than banked.** ⚠️ Banking it would empty a slot and see ten fish appear at once |
   * | Stopped only by the clock | **advanced by whole cooked items**, keeping the part-cooked one — {@link HearthState.litAtGameHours}'s own rule |
   */
  const cookFromGameHours =
    litAtGameHours === null || cook.every((n) => n <= 0)
      ? null
      : cookingStopped === 'noRoom'
        ? burnUntil
        : (state.cookFromGameHours ?? 0) + cooked * per;

  /* ⚫ Charcoal LAST, into whatever room the food left. */
  const ash = ashOf(HEARTH_FUEL);
  const makes = rules.makesCharcoal && ash !== undefined ? logsBurnt : 0;
  const put =
    makes > 0 && ash !== undefined ? putInOutput(shelf, ash, makes) : { ...shelf, stored: 0 };

  const next: HearthState = {
    wood,
    woodItem,
    output: put.output,
    outputItem: put.outputItem,
    cook,
    cookItem,
    litAtGameHours,
    cookFromGameHours,
  };

  return {
    state: next,
    logsBurnt,
    charcoalMade: put.stored,
    charcoalLost: makes - put.stored,
    cooked,
    cookingStopped,
    changed:
      logsBurnt > 0 ||
      cooked > 0 ||
      cookFromGameHours !== state.cookFromGameHours ||
      litAtGameHours !== state.litAtGameHours,
  };
}

/** Is this hearth burning right now? Resolve it first, or this is yesterday's answer. */
export function isBurning(state: HearthState): boolean {
  return state.litAtGameHours !== null && woodTotal(state) > 0;
}

/**
 * Game hours left in a resolved hearth. `0` when it is out.
 *
 * ⚠️ **Resolve first.** This trusts the state it is given.
 */
export function fuelLeftGameHours(state: HearthState, nowGameHours: number): number {
  if (!isBurning(state)) return 0;
  const total = woodTotal(state) * burnGameHoursOf(HEARTH_FUEL);
  const into = nowGameHours - (state.litAtGameHours ?? nowGameHours);
  const left = total - into;
  return left > 0 ? left : 0;
}

/** Is there room for another log? Two slots of 99. */
export function hasRoomForLog(state: HearthState): boolean {
  return state.wood.some((n) => n < HEARTH_STACK);
}

/**
 * Put one log in, lighting the fire if it was out.
 *
 * 🔴 **Lighting is FREE and needs no tool** — D86. ⚠️ **Not because that is
 * realistic:** there is no flint in Iceland (D45) and the real method was an
 * imported fire steel, so **a fire needing a tool nobody can obtain would ship
 * unreachable** — the exact fault this task exists to fix. 👉 Revisit when the
 * kaupskip trades.
 *
 * ⚠️ **Resolve before calling this**, or a log that finished burning an hour ago
 * is still counted as being in the fire.
 *
 * @returns the new state, or `null` if both wood slots are full.
 */
export function addLog(
  state: HearthState,
  nowGameHours: number,
  itemKey: string = HEARTH_FUEL,
): HearthState | null {
  /**
   * The FIRST slot with room takes it, so a fire fills up in a predictable
   * order rather than scattering logs across both slots.
   *
   * 🔴 **"With room" now also means "not already holding a DIFFERENT wood".** A
   * slot stores a count, so it can only be one kind of thing; a birch slot
   * declines driftwood and the log looks at the next slot instead.
   *
   * ⚠️ **This changes nothing about the fire.** Both woods burn for the same six
   * game hours and leave the same ash — it is about the hearth being able to say
   * what is inside it.
   */
  const roomFor = (i: number): boolean => {
    const count = state.wood.at(i) ?? 0;
    if (count >= HEARTH_STACK) return false;
    const here = state.woodItem.at(i) ?? null;
    return here === null || here === itemKey;
  };

  const target = state.wood.findIndex((_unused, i) => roomFor(i));
  if (target === -1) return null;

  return {
    ...state,
    wood: state.wood.map((count, i) => (i === target ? count + 1 : count)),
    woodItem: state.woodItem.map((here, i) => (i === target ? itemKey : here)),
    /* Already burning? Leave the marker where it is — the log at the front is
       part-burnt and keeps its progress. */
    litAtGameHours: state.litAtGameHours ?? nowGameHours,
    /**
     * 🍲 **Lighting a fire that already has food in it starts the cooking too**
     * — D121.
     *
     * 🔴 **Without this line, food loaded into a cold fire would never cook.**
     * The marker is set when food goes IN, and a cold fire clears it again on
     * the next resolve; so a player who loads the fish first and lights it
     * second — which is the natural order, and the order a cooking screen
     * invites — would have stood watching a lit fire do nothing. ⚠️ **And
     * nothing would have errored.**
     */
    cookFromGameHours: state.cookFromGameHours ?? (cookTotal(state) > 0 ? nowGameHours : null),
  };
}

/**
 * Everything in the hearth, as item keys and counts — for emptying it into a
 * pack when the block is broken.
 *
 * 🎯 **D96, haugur, 2026-08-27:** breaking the hearth *"move[s] it back into
 * inventory"*, **and what is inside comes with it.** ⚠️ **That closes the open
 * question this design had**: until now, breaking a hearth destroyed its
 * contents silently, and the hearth is the one place death cannot reach.
 */
export function hearthContents(state: HearthState): { itemKey: string; count: number }[] {
  const out: { itemKey: string; count: number }[] = [];
  const ash = ashOf(HEARTH_FUEL);
  /**
   * 🔴 **Per SLOT, so a hearth holding birch in one and driftwood in the other
   * gives back BOTH** — not two of whichever the code guessed.
   *
   * ⚠️ **Merged by item, though**, so two slots of the same wood come back as
   * one stack. The caller pours this into a pack, and handing it the same key
   * twice would make two stacks where the player expects one.
   */
  const byItem = new Map<string, number>();
  for (const [i, count] of state.wood.entries()) {
    if (count <= 0) continue;
    const key = state.woodItem.at(i) ?? HEARTH_FUEL;
    byItem.set(key, (byItem.get(key) ?? 0) + count);
  }
  /**
   * 🍲 **The input and the output shelf, per slot, merged the same way.**
   *
   * 🔴 **This used to add all four output slots up as CHARCOAL**, because
   * charcoal was the only thing that could be in them. Since D121 a slot may
   * hold a cooked fish — so breaking a hearth would have handed the player four
   * charcoal in place of their dinner. ⚠️ **A silent swap, not an error**, and
   * it is the fault `woodItem` was added to fix arriving one column to the
   * right for the second time.
   */
  for (const [i, count] of state.cook.entries()) {
    if (count <= 0) continue;
    const key = state.cookItem.at(i) ?? null;
    if (key !== null) byItem.set(key, (byItem.get(key) ?? 0) + count);
  }
  for (const [i, count] of state.output.entries()) {
    if (count <= 0) continue;
    /* ⚠️ A row written before D121 has no `outputItem`. `readHearth` fills it
       with charcoal on the way in, so this only ever falls back for a state
       built by hand in a test. */
    const key = state.outputItem.at(i) ?? ash ?? null;
    if (key !== null) byItem.set(key, (byItem.get(key) ?? 0) + count);
  }
  for (const [itemKey, count] of byItem) out.push({ itemKey, count });
  return out;
}

/**
 * 🔥 **One end of a move — which container, and which slot in it** (T128).
 *
 * 🔴 **The container is NAMED rather than implied by the number**, and that is
 * the whole reason this type exists. Both containers start at slot 0, so a bare
 * integer would mean two different boxes on two different tables — and the
 * failure that produces is the worst kind: **the right count of the right item
 * arrives in the wrong place, and nothing errors.**
 *
 * ⚠️ `Development/06_Traps.md` says the same thing about the wire: *adding a
 * parameter where an OPTIONAL one already lives changes what old calls mean.*
 * A named bag cannot be misread by an older reader; it is simply rejected.
 */
export interface HearthEnd {
  readonly where: 'hearth' | 'player';
  /**
   * 🔥 A hearth slot is **0–1 fuel, 2–5 output, 6–7 what is to be cooked** —
   * see {@link HEARTH_SLOT_COUNT}. A player slot is 0–33.
   */
  readonly slot: number;
}

/**
 * What one hearth slot actually holds, or `undefined` if it holds nothing.
 *
 * 🔴 **Every one of the eight answers from the STATE now, where the output
 * slots used to answer from a constant** — `ashOf(HEARTH_FUEL)`, which was true
 * while charcoal was the only thing a fire could make. ⚠️ **Since D121 that
 * constant would have called a cooked fish *charcoal*, and the player would have
 * dragged out charcoal**: the right count of the wrong item, with nothing
 * raised anywhere.
 */
export function hearthSlotItem(state: HearthState, slot: number): string | undefined {
  if (!Number.isInteger(slot) || slot < 0) return undefined;
  if (slot < HEARTH_WOOD_SLOTS) return state.woodItem.at(slot) ?? undefined;
  if (slot < COOK_FROM) return state.outputItem.at(slot - OUTPUT_FROM) ?? undefined;
  if (slot < HEARTH_SLOT_COUNT) return state.cookItem.at(slot - COOK_FROM) ?? undefined;
  return undefined;
}

/**
 * ⚫🍲 **Is this something a fire MAKES?** — charcoal, or any cooked food.
 *
 * 🔴 **Worked out from the data and never listed.** A fire's shelf takes back
 * what a fire puts on it, so the question is *"is this the `leavesBehind` of a
 * fuel, or the `cooksTo` of a food"* — and the day a fifth fish arrives, or a
 * second fuel leaves a different ash, this is already right. ⚠️ **A hard-coded
 * list of keys is the fault `campfireAccepts` was written around**, and the one
 * this repo keeps paying for.
 */
function isFireProduct(itemKey: string): boolean {
  if (itemKey === ashOf(HEARTH_FUEL)) return true;
  return COOKED_KEYS.has(itemKey);
}

/** Every item that is something else's cooked form, built once. */
const COOKED_KEYS: ReadonlySet<string> = new Set(
  ITEMS.flatMap((i) => (i.cooksTo === undefined ? [] : [i.cooksTo])),
);

/**
 * 🔥 **Would this slot take that item?**
 *
 * 🔴 **An EMPTY wood slot takes any fuel the fire accepts**, and that is the
 * whole correction. Before today it took {@link HEARTH_FUEL} and nothing else,
 * so driftwood could be burnt by right-clicking the fire and could **not** be
 * dragged into the panel — one feature answering two different ways. 🎯 haugur
 * found it in play: *"i placed 2 but there where not 2 on stock in the hearth."*
 *
 * ⚠️ **A slot that already holds something takes only more of that**, because a
 * slot stores a count and a count has to be a count OF something.
 */
export function hearthSlotAccepts(
  state: HearthState,
  slot: number,
  itemKey: string,
  rules: FireRules = HEARTH_RULES,
): boolean {
  if (!Number.isInteger(slot) || slot < 0) return false;

  /* ⚠️ The same two questions everywhere below: an EMPTY slot asks what KIND of
     thing belongs here, and an occupied one asks only for more of what it has,
     because a slot stores a count and a count has to be a count OF something. */
  const heldAt = (here: string | null, kind: (k: string) => boolean): boolean =>
    here === null ? kind(itemKey) : here === itemKey;

  if (slot < HEARTH_WOOD_SLOTS) {
    return heldAt(state.woodItem.at(slot) ?? null, campfireAccepts);
  }
  /* 🔥 D135 — a fire that does not cook has no shelf and no pot: the oven. */
  if (!rules.cooks) return false;
  if (slot < COOK_FROM) {
    /**
     * ⚫🍲 **The shelf takes back anything a fire makes** — 🎯 haugur's own
     * *"that adds the role of charcoal storage to the heath"*, now that the
     * shelf holds dinner as well.
     *
     * 🔴 **A fire that makes no charcoal will not STORE charcoal either, and
     * that is D71 rather than tidiness.** The búr refuses everything which is
     * not food; without this line a player could simply drag charcoal into the
     * búr's own shelf and keep it in the food house — **the locked rule broken
     * through a door nobody thought of, by hand, silently.**
     */
    const takes = (k: string): boolean =>
      isFireProduct(k) && (rules.makesCharcoal || k !== ashOf(HEARTH_FUEL));
    return heldAt(state.outputItem.at(slot - OUTPUT_FROM) ?? null, takes);
  }
  if (slot < HEARTH_SLOT_COUNT) {
    /* 🍲 Anything that names what it becomes. That IS the definition of
       cookable — see `cooksTo` in `items.ts`. */
    return heldAt(state.cookItem.at(slot - COOK_FROM) ?? null, (k) => cooksTo(k) !== undefined);
  }
  return false;
}

export interface HearthMoveResult {
  readonly state: HearthState;
  readonly stacks: readonly Stack[];
  /**
   * ⚠️ **`false` is not an error**, exactly as in {@link moveStack}. It is the
   * answer to a move that is not allowed, and the caller's job is to send both
   * the pack and the hearth back unchanged so the screen stops disagreeing.
   * 🔴 **Nothing is ever clamped to fit** — a silently adjusted move is how a
   * player ends up holding a number nobody chose.
   */
  readonly moved: boolean;
}

/**
 * 🔥 **Move `count` items between a player's pack and a hearth** — T128, D96.
 *
 * 🎯 **haugur, 2026-08-27:** *"i would also want to be able to move wood and
 * chacoal from my onhand inventory with the mouse, that adds the role of
 * charcoal storage to the heath."*
 *
 * ## 🔴 The slots enforce the meaning, so no refusal has to be explained
 *
 * A wood slot takes **only** {@link HEARTH_FUEL}; a charcoal slot takes **only**
 * what a log leaves behind. ⚠️ **That is D96 working as designed** — the file's
 * own opening says it: *"the slots themselves enforce wood only, so the refusal
 * stops being a message and becomes a property of the shape."* 👉 It is also
 * how **D90** — *"campfire needs trees as fuel, charcoal cannot be used"* —
 * survives dragging: charcoal simply does not fit in a wood slot.
 *
 * ## ⚠️ There is no swap, and that is deliberate
 *
 * {@link moveStack} swaps two player slots holding different items. A hearth
 * slot can only ever hold its one item, so a swap has nowhere to put what came
 * out. **Refused, unchanged**, which is the same answer as a full destination.
 *
 * ⚠️ **Both ends being `player` is refused too** — that is {@link moveStack}'s
 * job, and two functions that both half-answer one question is how they drift.
 *
 * 🔴 **This does NOT resolve the fire.** Burn the hearth up to date with
 * {@link resolveHearth} *before* calling this, or a log removed here is a log
 * that already burned.
 */
export function moveHearthItem(
  state: HearthState,
  stacks: readonly Stack[],
  from: HearthEnd,
  to: HearthEnd,
  count: number,
  /**
   * 🍲 **Absolute game hours** — needed because putting food into a cold input
   * slot is what STARTS the cooking clock. ⚠️ **Required rather than optional,
   * on purpose:** every existing caller fails to compile until it passes one,
   * which is the loud version of a change that would otherwise have left every
   * fish cooking from the beginning of time.
   */
  nowGameHours: number,
  /** Whether this fire makes charcoal — {@link HEARTH_RULES} or {@link BUR_FIRE_RULES}. */
  rules: FireRules = HEARTH_RULES,
): HearthMoveResult {
  const unchanged: HearthMoveResult = { state, stacks: [...stacks], moved: false };

  if (!Number.isInteger(count) || count <= 0) return unchanged;
  if (from.where === to.where) return unchanged;

  const slots = [...state.wood, ...state.output, ...state.cook];
  const hearthEnd = from.where === 'hearth' ? from : to;
  const playerEnd = from.where === 'player' ? from : to;

  /* 🔴 Two different questions, and conflating them was the bug. What the slot
     HOLDS decides what can come out; what it would ACCEPT decides what can go
     in. They differ exactly for an empty slot. */
  if (hearthEnd.slot < 0 || hearthEnd.slot >= HEARTH_SLOT_COUNT) {
    return unchanged;
  }
  const holds = hearthSlotItem(state, hearthEnd.slot);
  if (!isValidSlot(playerEnd.slot) || isClothingSlot(playerEnd.slot)) return unchanged;

  /* ⚠️ `at` rather than `slots[i]` — indexing with a variable is the
     object-injection pattern this file avoids throughout. */
  const at = (i: number): number => slots.find((_unused, j) => j === i) ?? 0;
  const held = at(hearthEnd.slot);

  const bySlot = new Map<number, Stack>(stacks.map((s) => [s.slot, s]));
  const inPack = bySlot.get(playerEnd.slot);

  if (from.where === 'player') {
    /* 🎒 → 🔥 Into the fire. */
    if (inPack === undefined || inPack.count < count) return unchanged;
    if (!hearthSlotAccepts(state, hearthEnd.slot, inPack.itemKey, rules)) return unchanged;
    if (held + count > stackLimit(inPack.itemKey)) return unchanged;
    if (inPack.count === count) bySlot.delete(playerEnd.slot);
    else bySlot.set(playerEnd.slot, { ...inPack, count: inPack.count - count });
    return {
      /* 🔥 A búr's fire lights itself the moment it has wood AND food. */
      state: kindle(
        writeHearthSlot(state, hearthEnd.slot, held + count, inPack.itemKey, nowGameHours),
        nowGameHours,
        rules,
      ),
      stacks: [...bySlot.values()],
      moved: true,
    };
  }

  /* 🔥 → 🎒 Out of the fire. ⚠️ `holds`, not "what the slot would accept": you
     take out whatever is actually in there, and an empty slot holds nothing. */
  if (held < count) return unchanged;
  if (holds === undefined) return unchanged;
  if (inPack === undefined) {
    if (!canGoIn(playerEnd.slot, holds, count)) return unchanged;
    bySlot.set(playerEnd.slot, { slot: playerEnd.slot, itemKey: holds, count });
  } else {
    if (inPack.itemKey !== holds) return unchanged;
    if (inPack.count + count > stackLimit(inPack.itemKey)) return unchanged;
    bySlot.set(playerEnd.slot, { ...inPack, count: inPack.count + count });
  }
  return {
    /* 🔥 Emptying a full shelf lets a búr's stopped fire light again. */
    state: kindle(
      writeHearthSlot(state, hearthEnd.slot, held - count, undefined, nowGameHours),
      nowGameHours,
      rules,
    ),
    stacks: [...bySlot.values()],
    moved: true,
  };
}

/**
 * Put a new count into one hearth slot, leaving the rest alone.
 *
 * 🔴 **A slot emptied to zero forgets what it held**, so the next thing is free
 * to take it; a slot given something remembers what that was. ⚠️ **The same
 * rule in all three groups now** — it was written for the wood slots and the
 * output shelf needs it for exactly the reason the wood did.
 */
function writeHearthSlot(
  state: HearthState,
  slot: number,
  count: number,
  itemKey: string | undefined,
  nowGameHours: number,
): HearthState {
  const group = slot < HEARTH_WOOD_SLOTS ? 'wood' : slot < COOK_FROM ? 'output' : 'cook';
  const within =
    group === 'wood' ? slot : group === 'output' ? slot - OUTPUT_FROM : slot - COOK_FROM;

  const counts = (list: readonly number[], mine: boolean): number[] =>
    list.map((n, i) => (mine && i === within ? count : n));
  const names = (list: readonly (string | null)[], mine: boolean): (string | null)[] =>
    list.map((here, i) => {
      if (!mine || i !== within) return here;
      if (count <= 0) return null;
      return itemKey ?? here;
    });

  const cook = counts(state.cook, group === 'cook');

  return {
    ...state,
    wood: counts(state.wood, group === 'wood'),
    woodItem: names(state.woodItem, group === 'wood'),
    output: counts(state.output, group === 'output'),
    outputItem: names(state.outputItem, group === 'output'),
    cook,
    cookItem: names(state.cookItem, group === 'cook'),
    /**
     * 🍲 **Putting food in starts the clock; taking the last of it out stops
     * it.** ⚠️ **And putting MORE in does NOT restart it** — the marker is only
     * set when it was `null`. That is {@link HearthState.litAtGameHours}'s own
     * rule, and without it a player topping up the input would lose the progress
     * on the fish already half cooked, every time, invisibly.
     */
    cookFromGameHours: cook.every((n) => n <= 0)
      ? null
      : (state.cookFromGameHours ?? (isBurning(state) ? nowGameHours : null)),
  };
}

/**
 * 🔥 **Is this block number a hearth — INCLUDING its lit state?**
 *
 * 🔴 **THE SERVER'S `isHearth` IS NOT ENOUGH, AND THE DIFFERENCE COST A HOUSE.**
 * `blockById` answers about **blocks**, which own 0…1023; a lit hearth is the
 * **state** `eldstaedi[lit=true]`, which lives at 1024 and is not in that map at
 * all. ⚠️ **So asking it about a burning fire says NO**, and a guard written
 * with it would refuse to put one out.
 *
 * ✅ **This asks the locked state table for the hearth's own numbers**, rather
 * than parsing a key or assuming a range.
 *
 * 👉 ***It is the same shape of mistake as `isBur` meaning "any of the three
 * houses"*** — a predicate that is *nearly* the question.
 */
export function isHearthBlock(id: number): boolean {
  const base = blockIdForKey(HEARTH_BLOCK_KEY);
  if (base === undefined) return false;
  if (id === base) return true;
  return withState(base, { lit: true }) === id || withState(base, { lit: false }) === id;
}

/** The block a hearth is made of. */
const HEARTH_BLOCK_KEY = 'eldstaedi';

/**
 * 🔴 **Which number the block should be, or `undefined` if it already agrees.**
 *
 * ⚠️ **It compares the BLOCK's state, not the raw number**, so a hearth that is
 * already correct costs no edit — and an edit here is a broadcast to everybody
 * plus a row in the audit log.
 *
 * ## 🔴 AND IT REFUSES ANYTHING THAT IS NOT A HEARTH, BECAUSE A HOUSE WAS LOST
 *
 * 🎯 **haugur, 2026-09-23:** *"i placed a wood in fuel to get ready for coocking
 * and then it just kicked me out and disapeared."*
 *
 * 📏 **The audit log, exactly:** `1029 → 34` at `347576, 14, -396311` —
 * **`bur[facing=east]` overwritten by an unlit `eldstaedi`.** The torches on its
 * walls survived, because they are their own blocks in their own cells; **the
 * house is drawn from that one anchor.**
 *
 * 🔴 **THE CAUSE IS D121 SHARING THE FIRE AND NOT THE BLOCK.** *"The fire cooks,
 * and it is the same machine in two places"* — ✅ **and the machine is right.**
 * ⚠️ **What came with it and should not have is the BLOCK WRITE.** A hearth says
 * whether it is burning **in the chunk**, as `eldstaedi[lit=true]`, because every
 * connected player is already looking at it. 🔴 **A búr has a fire and NO SUCH
 * STATE**, so there was nothing correct to write and it wrote a hearth over the
 * house.
 *
 * ⚠️ **The `occupied` guard could not help** — *"placing only into air… until
 * somebody paves over a building by mis-clicking"* — **because this is
 * `world.setBlock`, not a player edit.**
 *
 * ⚠️ **AND NOBODY HAD EVER FED A BÚR'S FIRE.** Nine light/unlight edits go back
 * to 4 September and **every one is at `347579, 14, -396315`, his real hearth**;
 * 🎯 *"it has worked before, i coocked a lot of fish"* is true and is about that
 * hearth. **The búr's Fire tab shipped the night before and had only been looked
 * at.**
 *
 * 👉 ***It is #737 again, three weeks later: a capability was given to one
 * member of a set, and nothing asserted which members it fits.***
 */
export function litStateFor(current: number, state: HearthState): number | undefined {
  if (!isHearthBlock(current)) return undefined;
  const wanted = withState(blockIdForKey(HEARTH_BLOCK_KEY) ?? 0, { lit: isBurning(state) });
  /* 🔴 The state was never locked. `withState` returns undefined rather than
     falling back to the base block for exactly this reason. */
  if (wanted === undefined) return undefined;
  return wanted === current ? undefined : wanted;
}
